import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Chip,
  Typography,
  CircularProgress
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { API_ENDPOINTS } from './constants/TrueViewTable.constants';
import { tableStyles, colors } from './styles/TrueViewTable.styles';
import { getApiBaseUrl } from './utils/TrueViewTable.utils';

interface EmailLog {
  id: number;
  recipient: string;
  subject: string;
  msg: string;
  status: string;
  sent_at: string;
}

interface EmailStatusPageProps {
  onGoBack: () => void;
}

const EmailStatusPage: React.FC<EmailStatusPageProps> = ({ onGoBack }) => {
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [recipientFilter, setRecipientFilter] = useState<string>('');
  const [subjectFilter, setSubjectFilter] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const fetchEmailLogs = async () => {
    setLoading(true);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (recipientFilter.trim()) {
        params.append('recipient', recipientFilter.trim());
      }
      
      if (subjectFilter.trim()) {
        params.append('subject', subjectFilter.trim());
      }

      const response = await fetch(`${apiBaseUrl}${API_ENDPOINTS.EMAIL_LOGS}?${params}`);
      const data = await response.json();
      setEmailLogs(data.data || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setEmailLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailLogs();
  }, [statusFilter, recipientFilter, subjectFilter, offset, limit]);

  const handleSearch = () => {
    setOffset(0);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    setOffset(prev => prev + limit);
    setCurrentPage(prev => prev + 1);
  };

  const handlePrevPage = () => {
    setOffset(prev => Math.max(0, prev - limit));
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const getStatusColor = (status: string) => {
    return status === 'sent' ? '#16a34a' : '#dc2626';
  };

  const getStatusBgColor = (status: string) => {
    return status === 'sent' ? '#dcfce7' : '#fee2e2';
  };

  return (
    <div style={tableStyles.container}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={tableStyles.card}>
          {/* Header */}
          <div style={{ ...tableStyles.header, padding: '24px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Button
                  onClick={onGoBack}
                  startIcon={<ArrowBack />}
                  sx={{
                    padding: '8px 16px',
                    backgroundColor: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    textTransform: 'none',
                    '&:hover': { 
                      backgroundColor: '#4f46e5'
                    }
                  }}
                >
                  Back to TrueView
                </Button>
                <div>
                  <h1 style={{
                    color: 'white',
                    fontSize: '28px',
                    fontWeight: 'bold',
                    margin: '0 0 8px 0'
                  }}>
                    Email Status
                  </h1>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Recipient"
                value={recipientFilter}
                onChange={(e) => setRecipientFilter(e.target.value)}
                placeholder="Search by recipient email"
                sx={{ minWidth: 200 }}
              />

              <TextField
                size="small"
                label="Subject"
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                placeholder="Search by email subject"
                sx={{ minWidth: 200 }}
              />

              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={limit}
                  label="Per Page"
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setOffset(0);
                    setCurrentPage(1);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ backgroundColor: colors.primary }}
              >
                Search
              </Button>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '24px' }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                <CircularProgress />
              </div>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Recipient
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Subject
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Status
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Timestamp
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Error
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emailLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="textSecondary">
                              No email logs found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        emailLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell sx={{ fontSize: '14px' }}>
                              {log.recipient}
                            </TableCell>
                            <TableCell sx={{ fontSize: '14px', maxWidth: '300px' }}>
                              {log.subject}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={log.status.toUpperCase()}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusBgColor(log.status),
                                  color: getStatusColor(log.status),
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '14px' }}>
                              {new Date(log.sent_at).toLocaleString()}
                            </TableCell>
                            <TableCell sx={{ fontSize: '14px', maxWidth: '200px' }}>
                              {log.status === 'failed' ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#dc2626',
                                    fontSize: '12px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  Failed to send
                                </Typography>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px'
                }}>
                  <Typography variant="body2" color="textSecondary">
                    Page {currentPage} - Showing {offset + 1} - {Math.min(offset + limit, offset + emailLogs.length)} of {totalCount > 0 ? totalCount : 'many'} results
                  </Typography>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      variant="outlined"
                      onClick={handlePrevPage}
                      disabled={offset === 0}
                      size="small"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleNextPage}
                      disabled={emailLogs.length < limit}
                      size="small"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailStatusPage;